import { useAuth } from 'contexts/AuthContext'
import { generateOpticStockNumber, generateShieldStockNumber } from 'core/inventory/generateStockNumber'
import { sanitizeVehicleData } from 'core/utils/inventoryUtils'
import { useInventory } from 'hooks/inventory'
import React from 'react'
import VehicleForm from './VehicleForm'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { DocumentData } from 'firebase/firestore'

interface EditVehicleFormProps {
  vehicleId?: string
  open: boolean
  onClose: () => void
  inventoryCollection?: 'master_inventory' | 'shield_inventory'
  onEditSuccess?: () => void
  disableSnackbarUpdates?: boolean
  onStatusChange?: (vehicleId: string, data: any, status: string) => void
  canViewPurchasePrice?: boolean
  canViewProfit?: boolean
}

const EditVehicleForm: React.FC<EditVehicleFormProps> = ({
  vehicleId,
  open,
  onClose,
  inventoryCollection = 'master_inventory',
  onEditSuccess,
  disableSnackbarUpdates = false,
  onStatusChange,
  canViewPurchasePrice = true,
  canViewProfit = true,
}) => {
  if (!vehicleId) {
    return null
  }

  const { userInfo } = useAuth()
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()

  // Use the useInventory hook to get all vehicles and update functionality
  const { vehicles, updateDocument, isLoading } = useInventory(inventoryCollection, undefined, {
    dontShowUpdates: disableSnackbarUpdates,
  })

  // Memoize the selected vehicle for better performance
  const vehicle = React.useMemo(() => {
    if (!vehicles || !vehicleId) return null
    return vehicles.find(v => v.id === vehicleId) || null
  }, [vehicles, vehicleId])

  if (isLoading || !vehicle) {
    return null
  }

  const generateStockNumber = () => {
    const existingStockNumbers: string[] = vehicles ? vehicles.map(vehicle => vehicle.stock_number) : []

    if (inventoryCollection === 'shield_inventory') {
      return generateShieldStockNumber(existingStockNumbers)
    } else {
      // For master_inventory, use the user's name to generate the stock number
      const firstName = userInfo?.first_name || ''
      const lastName = userInfo?.last_name || ''
      return generateOpticStockNumber(existingStockNumbers, firstName, lastName)
    }
  }

  const handleVehicleDataUpdate = async (vehicleId: string, data: { [key: string]: any }): Promise<boolean> => {
    // Find the vehicle from the collection
    const vehicle = vehicles?.find(v => v.id === vehicleId)

    // Check if the vehicle already has a sold_date
    const hasSoldDate = !!vehicle?.sold_date
    const isChangingSoldDate = data.hasOwnProperty('sold_date') && data.sold_date !== vehicle?.sold_date

    // Only owner can modify sold_date if it already exists
    if (hasSoldDate && isChangingSoldDate && !userInfo?.isOwner) {
      showError('Only the owner can modify the sold date of a vehicle')
      return false
    }

    const sanitizedData = sanitizeVehicleData(data, vehicle)

    if (!sanitizedData) {
      console.error('Data sanitization failed')
      return false
    }

    // Check for status changes that need special handling
    if (sanitizedData.status) {
      const newStatus = typeof sanitizedData.status === 'string' ? sanitizedData.status.toLowerCase().trim() : ''
      const currentStatus = vehicle?.status?.toLowerCase().trim() || ''

      // Check for status changes that need special handling
      // Only trigger sold dialog if changing TO sold (not if already sold)
      const isChangingToSold = newStatus === 'sold' && currentStatus !== 'sold' && currentStatus !== 'sold elsewhere'
      const isChangingToPendingAvailable = newStatus === 'pending available'
      const isChangingToSoldElsewhere = newStatus === 'sold elsewhere'
      const hasDateAvailable = !!vehicle?.date_available || !!data.date_available

      // If changing to Sold and we don't have a sold date
      if (isChangingToSold && onStatusChange) {
        // Remove status so it can be set by the dialog handler
        const dataForDialog = { ...sanitizedData }
        delete dataForDialog.status

        // Call parent handler to show the sold dialog
        onStatusChange(vehicleId, dataForDialog, 'sold')
        return true
      }

      // If changing to Pending Available and we don't have a date available
      if (isChangingToPendingAvailable && !hasDateAvailable && onStatusChange) {
        // Remove status so it can be set by the dialog handler
        const dataForDialog = { ...sanitizedData }
        delete dataForDialog.status

        // Call parent handler to show the date availability dialog
        onStatusChange(vehicleId, dataForDialog, 'pending available')
        return true
      }

      if (isChangingToSoldElsewhere && onStatusChange) {
        sanitizedData.show_on_website = false
      }
    }

    try {
      await updateDocument({
        id: vehicleId,
        data: sanitizedData as Partial<DocumentData>,
      })

      if (onEditSuccess) {
        onEditSuccess()
      }

      return true
    } catch (error) {
      console.error('Error updating vehicle:', error)
      showError('Error updating vehicle')
      return false
    }
  }

  const handleEditVehicleForm = async (data: { [key: string]: string | number | boolean }) => {
    const success = await handleVehicleDataUpdate(vehicleId, data)
    if (success) {
      onClose()
    }
  }

  return (
    <VehicleForm
      open={open}
      vehicle={vehicle}
      onClose={onClose}
      onSubmit={handleEditVehicleForm}
      inventoryCollection={inventoryCollection}
      generateStockNumber={generateStockNumber}
      isEdit={true}
      canViewPurchasePrice={canViewPurchasePrice}
      canViewProfit={canViewProfit}
    />
  )
}

export default EditVehicleForm
